import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Text } from '../components/Texte';
import { MaterialCommunityIcons } from '../components/icons';
import { FondAube } from '../components/FondAube';
import { FondNuit } from '../components/FondNuit';
import { GradientText } from '../components/GradientText';
import { ScoreRing } from '../components/ScoreRing';
import { t } from '../i18n';
import { styleMiroir } from '../i18n/sens';
import { FEATURE_ACCENTS } from '../theme/featureAccents';
import { useGabarit } from '../hooks/useGabarit';
import { useCarriereApercu, type CarriereApercu } from '../hooks/useCarriereApercu';
import { colors, gradients, layout, radius, shadow, spacing, typography } from '../theme/tokens';
import { LARGEUR_EN_RANGEE } from '../theme/miseEnPage';

export interface CarriereLargeProps {
  onOpenMyCv: () => void;
  onOpenLinkedin: () => void;
  onOpenLetter: () => void;
  onOpenInterview: () => void;
}

interface Outil {
  key: 'cv' | 'letter' | 'linkedin' | 'interview';
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  title: string;
  description: string;
  cta: string;
  color: string;
  onPress: () => void;
  testID: string;
  statut: string;
  /** Vrai quand la personne a déjà commencé avec cet outil : le point d'état passe au vert. */
  fait: boolean;
}

const TAILLE_ANNEAU = 104;
const LARGEUR_CHIFFRE = 128;
const ECART_CARTES = spacing.lg;

/** Transition de survol, propre au web : react-native ne la type pas, react-native-web la transmet. */
const TRANSITION = { transitionProperty: 'transform, box-shadow, border-color', transitionDuration: '160ms' } as object;

const ATTENTE = '…';

/** Construit les quatre outils avec leur état réel (CV, lettres), dans l'ordre du parcours. */
function construireOutils(p: CarriereLargeProps, d: CarriereApercu): Outil[] {
  const statutCv =
    d.cvCount === null
      ? ATTENTE
      : d.cvCount === 0
        ? t('carriere.tableau.statutCvAucun')
        : [
            t('carriere.tableau.statutCv', { count: d.cvCount }),
            d.score !== null ? t('carriere.tableau.statutScore', { score: d.score }) : null,
          ]
            .filter(Boolean)
            .join(' · ');
  const statutLettres =
    d.lettres === null
      ? ATTENTE
      : d.lettres === 0
        ? t('carriere.tableau.statutLettreAucune')
        : t('carriere.tableau.statutLettres', { count: d.lettres });

  return [
    {
      key: 'cv',
      icon: 'file-document-outline',
      title: t('carriere.cv.title'),
      description: t('carriere.cv.description'),
      cta: t('carriere.cv.cta'),
      color: FEATURE_ACCENTS.cv.color,
      onPress: p.onOpenMyCv,
      testID: 'carriere-cv',
      statut: statutCv,
      fait: (d.cvCount ?? 0) > 0,
    },
    {
      key: 'letter',
      icon: 'email-edit-outline',
      title: t('carriere.letter.title'),
      description: t('carriere.letter.description'),
      cta: t('carriere.letter.cta'),
      color: FEATURE_ACCENTS.letter.color,
      onPress: p.onOpenLetter,
      testID: 'carriere-letter',
      statut: statutLettres,
      fait: (d.lettres ?? 0) > 0,
    },
    {
      key: 'linkedin',
      icon: 'linkedin',
      title: t('carriere.linkedin.title'),
      description: t('carriere.linkedin.description'),
      cta: t('carriere.linkedin.cta'),
      color: FEATURE_ACCENTS.linkedin.color,
      onPress: p.onOpenLinkedin,
      testID: 'carriere-linkedin',
      statut: t('carriere.tableau.statutLinkedin'),
      fait: false,
    },
    {
      key: 'interview',
      icon: 'microphone-outline',
      title: t('carriere.interview.title'),
      description: t('carriere.interview.description'),
      cta: t('carriere.interview.cta'),
      color: FEATURE_ACCENTS.interview.color,
      onPress: p.onOpenInterview,
      testID: 'carriere-interview',
      statut: t('carriere.tableau.statutInterview'),
      fait: false,
    },
  ];
}

function Fleche({ couleur }: { couleur: string }) {
  return (
    <View style={styleMiroir()}>
      <MaterialCommunityIcons name="arrow-right" size={typography.bodyMd.size} color={couleur} />
    </View>
  );
}

/**
 * Carte d'un outil : fond blanc, filet de la couleur de l'outil en haut, pastille d'icône, l'état
 * réel de la personne pour cet outil, puis l'appel à l'action en lien. Au survol elle se soulève et
 * sa bordure prend la couleur de l'outil.
 */
function CarteOutil({ outil }: { outil: Outil }) {
  const [survol, setSurvol] = useState(false);
  const { icon, title, description, cta, color, onPress, testID, statut, fait } = outil;

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessible
      accessibilityLabel={`${title}. ${description} ${cta}`}
      onPress={onPress}
      onHoverIn={() => setSurvol(true)}
      onHoverOut={() => setSurvol(false)}
      style={[styles.carte, survol && { ...styles.carteSurvol, borderColor: color }, TRANSITION]}
    >
      <View style={[styles.filet, { backgroundColor: color }]} />
      <View style={[styles.pastille, { backgroundColor: color }]}>
        <MaterialCommunityIcons testID={`${testID}-icon`} name={icon} size={spacing.xl + spacing.sm} color={colors.white} />
      </View>
      <Text style={styles.carteTitre}>{title}</Text>
      <Text style={styles.carteDescription}>{description}</Text>
      <View style={styles.pied}>
        <View style={styles.statut}>
          <View style={[styles.point, { backgroundColor: fait ? colors.success : colors.grey400 }]} />
          <Text style={styles.statutTexte}>{statut}</Text>
        </View>
        <View testID={`${testID}-cta`} style={styles.lien}>
          <Text style={[styles.lienTexte, { color }]}>{cta}</Text>
          <Fleche couleur={color} />
        </View>
      </View>
    </Pressable>
  );
}

/**
 * Carrière sur ordinateur et tablette : un tableau de bord, pas un carrousel.
 *
 * Le carrousel à voisines inclinées est un geste de pouce ; avec une souris il cache trois outils
 * sur quatre derrière un geste de défilement. Ici la page dit d'abord où la personne en est et
 * quel est son prochain pas (un seul, mis en avant), puis montre les quatre outils d'un coup, chacun
 * avec son état réel, et chacun s'ouvre en un clic.
 */
export function CarriereLarge(props: CarriereLargeProps) {
  const { etendu: gabaritEtendu, largeur } = useGabarit();
  const etendu = gabaritEtendu && largeur >= LARGEUR_EN_RANGEE;
  const d = useCarriereApercu();
  const outils = construireOutils(props, d);
  const sansScore = d.score === null;

  const tailleAccroche = etendu ? typography.h1.size + spacing.lg : typography.h1.size + spacing.sm;
  const styleAccroche = {
    fontSize: tailleAccroche,
    lineHeight: tailleAccroche + spacing.md,
    fontWeight: '800' as const,
    letterSpacing: -1,
    color: colors.navy,
  };

  const chiffres = [
    {
      cle: 'cv',
      nombre: d.cvCount === null ? '-' : String(d.cvCount),
      libelle: t('carriere.tableau.chiffreCv', { count: d.cvCount ?? 0 }),
    },
    {
      cle: 'lettres',
      nombre: d.lettres === null ? '-' : String(d.lettres),
      libelle: t('carriere.tableau.chiffreLettres', { count: d.lettres ?? 0 }),
    },
    {
      cle: 'corrections',
      nombre: sansScore ? '-' : String(d.nbConseils),
      libelle: t('carriere.tableau.chiffreCorrections', { count: sansScore ? 0 : d.nbConseils }),
    },
  ];

  return (
    <View style={styles.container}>
      <FondAube />
      <ScrollView
        testID="carriere-page"
        contentContainerStyle={[styles.content, !etendu && styles.contentMoyen]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.page}>
          <View style={styles.accroche}>
            <Text style={styleAccroche}>{t('carriere.headline').replace('\n', ' ')}</Text>
            <GradientText testID="carriere-headline-gradient" style={styleAccroche}>
              {t('carriere.headlineAccent')}
            </GradientText>
          </View>

          <FondNuit style={[styles.panneau, !etendu && styles.panneauMoyen]} testID="carriere-prochain-pas">
            <View style={styles.panneauPrincipal}>
              {sansScore ? (
                <View style={styles.anneauVide}>
                  <MaterialCommunityIcons name="file-search-outline" size={spacing.xxl + spacing.md} color={colors.white} />
                </View>
              ) : (
                <ScoreRing
                  score={d.score ?? 0}
                  size={TAILLE_ANNEAU}
                  variante="degrade"
                  surFond="sombre"
                  legende={t('common.scoreSur100')}
                />
              )}
              <View style={styles.panneauTexte}>
                <Text style={styles.panneauTitre}>
                  {sansScore ? t('carriere.tableau.premierPas') : t('carriere.tableau.prochainPas')}
                </Text>
                <Text style={styles.panneauConseil} numberOfLines={2}>
                  {sansScore ? t('carriere.tableau.sansScore') : (d.conseil ?? t('carriere.tableau.conseilParDefaut'))}
                </Text>
                <Pressable
                  testID="carriere-prochain-pas-cta"
                  accessibilityRole="button"
                  onPress={props.onOpenMyCv}
                  style={styles.cta}
                >
                  <LinearGradient
                    colors={[...gradients.cta]}
                    start={{ x: 0, y: 0.5 }}
                    end={{ x: 1, y: 0.5 }}
                    style={StyleSheet.absoluteFill}
                  />
                  <Text style={styles.ctaTexte}>
                    {sansScore ? t('carriere.tableau.noter') : t('carriere.tableau.corriger')}
                  </Text>
                  <Fleche couleur={colors.white} />
                </Pressable>
              </View>
            </View>

            <View testID="carriere-chiffres" style={styles.chiffres}>
              {chiffres.map((c) => (
                <View key={c.cle} style={[styles.chiffre, etendu ? styles.chiffreEtendu : styles.chiffreMoyen]}>
                  <Text style={styles.chiffreNombre}>{c.nombre}</Text>
                  <Text style={styles.chiffreLibelle}>{c.libelle}</Text>
                </View>
              ))}
            </View>
          </FondNuit>

          {etendu ? (
            <View testID="carriere-tableau" style={styles.rangee}>
              {outils.map((o) => (
                <CarteOutil key={o.key} outil={o} />
              ))}
            </View>
          ) : (
            <View testID="carriere-tableau" style={styles.grilleMoyenne}>
              {outils.map((o) => (
                <View key={o.key} style={styles.celluleMoyenne}>
                  <CarteOutil outil={o} />
                </View>
              ))}
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  // Centré à la verticale : sur un écran bas la page se pose au milieu au lieu de coller en haut.
  content: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: spacing.xl,
    paddingHorizontal: spacing.xxl + spacing.xl,
  },
  contentMoyen: { paddingHorizontal: spacing.xxl },
  page: { width: '100%', maxWidth: layout.pageMaxWidth, alignSelf: 'center', gap: spacing.xl },

  // Accroche calée au bord de la page, comme un titre de site : le centrage était un reste du carrousel.
  accroche: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, alignItems: 'baseline' },

  panneau: {
    borderRadius: radius.xl,
    padding: spacing.xl + spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.xl,
    overflow: 'hidden',
  },
  panneauMoyen: { flexDirection: 'column', alignItems: 'stretch' },
  panneauPrincipal: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: spacing.xl },
  anneauVide: {
    width: TAILLE_ANNEAU,
    height: TAILLE_ANNEAU,
    borderRadius: TAILLE_ANNEAU / 2,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceNuitForte,
    borderWidth: 1,
    borderColor: colors.bordureNuit,
  },
  panneauTexte: { flex: 1, gap: spacing.sm, alignItems: 'flex-start' },
  panneauTitre: {
    fontSize: typography.h1.size,
    lineHeight: typography.h1.lineHeight,
    fontWeight: '800',
    color: colors.white,
    letterSpacing: -0.3,
  },
  panneauConseil: {
    fontSize: typography.body.size,
    lineHeight: typography.body.lineHeight,
    color: colors.blancAtenue,
    maxWidth: 640,
  },
  cta: {
    marginTop: spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderRadius: radius.pill,
    overflow: 'hidden',
    cursor: 'pointer',
  },
  ctaTexte: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.white },

  chiffres: { flexDirection: 'row', gap: spacing.md },
  chiffre: {
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.md,
    borderRadius: radius.lg,
    backgroundColor: colors.surfaceNuit,
    borderWidth: 1,
    borderColor: colors.bordureNuit,
    gap: spacing.xs,
  },
  chiffreEtendu: { width: LARGEUR_CHIFFRE },
  chiffreMoyen: { flex: 1 },
  chiffreNombre: { fontSize: 34, lineHeight: 38, fontWeight: '800', color: colors.white, letterSpacing: -1 },
  chiffreLibelle: { fontSize: typography.small.size, color: colors.blancAtenue },

  rangee: { flexDirection: 'row', gap: ECART_CARTES },
  grilleMoyenne: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -ECART_CARTES / 2, rowGap: ECART_CARTES },
  celluleMoyenne: { width: '50%', paddingHorizontal: ECART_CARTES / 2 },

  carte: {
    flex: 1,
    minHeight: 272,
    padding: spacing.xl,
    paddingTop: spacing.xl + spacing.sm,
    borderRadius: radius.xl,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: colors.white,
    overflow: 'hidden',
    cursor: 'pointer',
    gap: spacing.sm,
    ...shadow.card,
  },
  carteSurvol: { transform: [{ translateY: -3 }], ...shadow.floating },
  filet: { position: 'absolute', top: 0, left: 0, right: 0, height: 5 },
  pastille: {
    width: 52,
    height: 52,
    borderRadius: radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.xs,
  },
  carteTitre: {
    fontSize: typography.h1.size - 2,
    lineHeight: typography.h1.lineHeight,
    fontWeight: '800',
    color: colors.navy,
  },
  carteDescription: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey500 },
  pied: { marginTop: 'auto', gap: spacing.md, paddingTop: spacing.md },
  statut: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  point: { width: 8, height: 8, borderRadius: 4 },
  statutTexte: { fontSize: typography.small.size, fontWeight: '600', color: colors.grey700 },
  lien: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  lienTexte: { fontSize: typography.bodyMd.size, fontWeight: '700' },
});
